import type { UpdateWebhookTargetInput } from '@mesa/core/webhook/mutations';
import {
  createWebhookTarget,
  deleteWebhookTarget,
  getWebhookTarget,
  listWebhookTargets,
  regenerateWebhookSecret,
  updateWebhookTarget,
} from '@mesa/core/webhook/service';
import type { WebhookEventType } from '@mesa/core/webhook/types';
import { createServerFn } from '@tanstack/react-start';
import { baseAuthMiddleware } from '../middleware/base-auth';

// Thin adapters over @mesa/core/webhook/service, which the REST routes share.
// Scope and repo-visibility checks live in the service.

export const listWebhookTargetsServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ context: { auth } }) => {
    const targets = await listWebhookTargets(auth);
    return targets.map(({ webhookTarget, repos }) => ({ ...webhookTarget, repos }));
  });

export const getWebhookTargetServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { auth } }) => {
    const { webhookTarget, repos } = await getWebhookTarget(auth, webhookTargetId);
    return { ...webhookTarget, repos };
  });

export const createWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator(
    (data: {
      orgSlug: string;
      url: string;
      events: WebhookEventType[];
      repoIds: string[] | null;
      name?: string | null;
    }) => data
  )
  .handler(async ({ data: { url, events, repoIds, name }, context: { auth } }) => {
    const created = await createWebhookTarget(auth, { url, events, repoIds, name });
    return { ...created.webhookTarget, repoIds: created.repoIds, secret: created.secret };
  });

export const updateWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; webhookTargetId: string; patch: UpdateWebhookTargetInput }) => data)
  .handler(async ({ data: { webhookTargetId, patch }, context: { auth } }) => {
    const { webhookTarget, repoIds } = await updateWebhookTarget(auth, webhookTargetId, patch);
    return { ...webhookTarget, repoIds };
  });

export const regenerateWebhookSecretServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { auth } }) => {
    const { webhookTarget } = await regenerateWebhookSecret(auth, webhookTargetId);
    return webhookTarget;
  });

export const deleteWebhookTargetServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; webhookTargetId: string }) => data)
  .handler(async ({ data: { webhookTargetId }, context: { auth } }) => {
    await deleteWebhookTarget(auth, webhookTargetId);
  });
